/**
 * Taxonomy → glyph (and hue) map (REDESIGN §7b).
 *
 * The keys are the LIVE Financy taxonomy of `docs/financy-api.md` → LIVE DATA ADDENDUM, which
 * `shared/financy/src/categories.ts` turns into Hebrew labels. Every key that has a label there
 * has an explicit glyph here — `test/categories.test.ts` walks `MAIN_LABELS`, `SUB_LABELS` and
 * `NULL_MAIN_SUB_LABELS` and fails on any key that is missing or that only resolves through the
 * fallback, so a new category cannot ship without a drawn icon.
 *
 * No import of the financy library at runtime: this is a plain lookup table, and the two files
 * are tied together by the test rather than by a dependency.
 *
 * Hues are the soft background circle of §7b, capped at six plus two non-decorative ones
 * (`neutral`, `warn`) so a screen never shows more than six category hues at once (§8).
 */
import { FALLBACK_GLYPH } from './glyphs.ts';
import type { GlyphName } from './glyphs.ts';

/** The six decorative hues plus the two that carry meaning. */
export const CATEGORY_HUES = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'neutral', 'warn'] as const;
export type CategoryHue = (typeof CATEGORY_HUES)[number];

/** Glyph per `category.main`. */
export const MAIN_GLYPHS: Readonly<Record<string, GlyphName>> = {
  'INCOMES_EXPENSES': 'arrows',
  'TRANSFER': 'transfer',
  'FOOD_&_DRINKS': 'utensils',
  'SHOPPING': 'bag',
  'FINANCE': 'coins',
  'OTHER': 'dashed',
  'HOUSEHOLD_&_SERVICES': 'home',
  'DEPOSIT': 'banknote',
  'LOAN_TRANSACTION': 'loan',
  'TRANSPORT': 'car',
  'RETURN': 'undo',
  'INSURANCE': 'shield',
  'HOK_RETURN': 'alert',
  'CHQ_RETURN': 'cheque',
  'UNCATEGORIZED': 'dashed',
  'ASSETS': 'piggy',
  'NATIONAL_INSURANCE': 'landmark',
  'LEISURE': 'ticket',
  'HEALTH_&_BEAUTY': 'stethoscope',
  'VAT': 'receipt',
  'NEGATIVE_BEHAVIOR': 'alert',
  'HOME_IMPROVEMENTS': 'hammer',
  'PENSION': 'piggy',
  'TRADING': 'exchange',
};

/** Glyph per `MAIN/SUB` pair — the sub-categories that deserve their own picture. */
export const SUB_GLYPHS: Readonly<Record<string, GlyphName>> = {
  'INCOMES_EXPENSES/OTHER': 'arrows',
  'INCOMES_EXPENSES/CREDIT_CARD_CHECKING': 'card',
  'TRANSFER/BANK_TRANSFER': 'transfer',
  'TRANSFER/BIT_PAYBOX': 'phoneSend',
  'FOOD_&_DRINKS/RESTAURANT': 'utensils',
  'FOOD_&_DRINKS/GROCERIES': 'cart',
  'FOOD_&_DRINKS/COFFEE_&_SNACKS': 'coffee',
  'FOOD_&_DRINKS/ALCOHOL_&_TOBACCO': 'glass',
  'FOOD_&_DRINKS/BARS': 'glass',
  'SHOPPING/SHOPPING_OTHER': 'bag',
  'SHOPPING/ELECTRONICS': 'tv',
  'SHOPPING/CLOTHES_&_ACCESSORIES': 'shirt',
  'SHOPPING/BOOKS_&_GAMES': 'book',
  'SHOPPING/HOBBY_&_SPORTS_EQUIPMENT': 'dumbbell',
  'FINANCE/FINANCE_OTHER': 'coins',
  'FINANCE/FEES': 'percent',
  'OTHER/BUSINESS_EXPENSES': 'briefcase',
  'OTHER/OTHER': 'dashed',
  'OTHER/PETS': 'paw',
  'OTHER/EDUCATION': 'graduation',
  'HOUSEHOLD_&_SERVICES/COMMUNICATIONS': 'phone',
  'HOUSEHOLD_&_SERVICES/INSURANCE_&_FEES': 'shield',
  'HOUSEHOLD_&_SERVICES/UTILITIES': 'bolt',
  'DEPOSIT/CASH_INCOME': 'banknote',
  'DEPOSIT/CHQ_INCOME': 'cheque',
  'LOAN_TRANSACTION/LOAN': 'loan',
  'LOAN_TRANSACTION/MORTGAGE': 'homeLoan',
  'TRANSPORT/TAXI': 'taxi',
  'TRANSPORT/CAR_&_FUEL': 'fuel',
  'TRANSPORT/TRANSPORT_OTHER': 'bus',
  'RETURN/NON_REVENUE': 'undo',
  'RETURN/REFUND': 'undo',
  'INSURANCE/INSURANCE': 'shield',
  'HOK_RETURN/HOK_AKAM_COMMISSION': 'percent',
  'HOK_RETURN/FUNDS_NOT_COVER': 'alert',
  'HOK_RETURN/COMMISSION': 'percent',
  'HOK_RETURN/NOT_CLASSIFIED': 'alert',
  'CHQ_RETURN/FUNDS_NOT_COVER': 'alert',
  'CHQ_RETURN/CHQ_AKAM_COMMISSION': 'percent',
  'CHQ_RETURN/CHQ_COMMISSION': 'percent',
  'CHQ_RETURN/NOT_CLASSIFIED': 'cheque',
  'CHQ_RETURN/TECHNI': 'cheque',
  'UNCATEGORIZED/UNCATEGORIZED': 'dashed',
  'ASSETS/SAVINGS': 'piggy',
  'ASSETS/INTEREST_RATE': 'percent',
  'NATIONAL_INSURANCE/NOT_RESERVE_DUTY': 'landmark',
  'LEISURE/LEISURE_OTHER': 'ticket',
  'LEISURE/VACATION': 'plane',
  'LEISURE/CULTURE_&_EVENTS': 'ticket',
  'LEISURE/HOBBIES': 'palette',
  'HEALTH_&_BEAUTY/HEALTHCARE': 'stethoscope',
  'HEALTH_&_BEAUTY/PHARMACY': 'pill',
  'HEALTH_&_BEAUTY/EYECARE': 'eye',
  'HEALTH_&_BEAUTY/BEAUTY': 'sparkle',
  'VAT/VAT': 'receipt',
  'NEGATIVE_BEHAVIOR/ALERT_NOTICE': 'alert',
  'HOME_IMPROVEMENTS/RENOVATION_&_REPAIRS': 'hammer',
  'HOME_IMPROVEMENTS/FURNITURE_&_INTERIOR': 'sofa',
  'HOME_IMPROVEMENTS/GARDEN': 'sprout',
  'PENSION/RECENT_PENSION': 'piggy',
  'TRADING/FOREIGN_EXCHANGE': 'exchange',
};

/** Glyph for the subs that arrive with `main: null` (live: 56 rows). */
export const NULL_MAIN_SUB_GLYPHS: Readonly<Record<string, GlyphName>> = {
  'GOVERNMENT SERVICES': 'landmark',
  'SHOPPING_OTHER': 'bag',
  'BUSINESS_EXPENSES': 'briefcase',
  'HEALTHCARE': 'stethoscope',
  'ELECTRONICS': 'tv',
  'BOOKS_&_GAMES': 'book',
};

/**
 * Glyph per personal display group (`shared/financy` `GROUP_KEYS`) — the category screen, the
 * budget rows and the deviation lines all key off the group, not the raw taxonomy.
 */
export const GROUP_GLYPHS: Readonly<Record<string, GlyphName>> = {
  HOUSING: 'home',
  GROCERIES: 'cart',
  KIDS: 'graduation',
  TRANSPORT: 'car',
  DINING: 'utensils',
  SHOPPING: 'bag',
  INSURANCE: 'shield',
  HEALTH: 'stethoscope',
  TELECOM: 'phone',
  OTHER: 'dashed',
};

/** Hue per `category.main`. Anything not listed is `neutral`. */
export const MAIN_HUES: Readonly<Record<string, CategoryHue>> = {
  'INCOMES_EXPENSES': 'h1',
  'TRANSFER': 'h1',
  'HOUSEHOLD_&_SERVICES': 'h1',
  'FOOD_&_DRINKS': 'h2',
  'LEISURE': 'h2',
  'TRANSPORT': 'h2',
  'DEPOSIT': 'h3',
  'ASSETS': 'h3',
  'PENSION': 'h3',
  'RETURN': 'h3',
  'SHOPPING': 'h4',
  'HOME_IMPROVEMENTS': 'h4',
  'HEALTH_&_BEAUTY': 'h5',
  'INSURANCE': 'h5',
  'FINANCE': 'h6',
  'LOAN_TRANSACTION': 'h6',
  'VAT': 'h6',
  'NATIONAL_INSURANCE': 'h6',
  'TRADING': 'h6',
  'OTHER': 'neutral',
  'UNCATEGORIZED': 'neutral',
  'HOK_RETURN': 'warn',
  'CHQ_RETURN': 'warn',
  'NEGATIVE_BEHAVIOR': 'warn',
};

/** Hue per display group. */
export const GROUP_HUES: Readonly<Record<string, CategoryHue>> = {
  HOUSING: 'h1',
  TELECOM: 'h1',
  DINING: 'h2',
  TRANSPORT: 'h2',
  GROCERIES: 'h3',
  SHOPPING: 'h4',
  INSURANCE: 'h5',
  HEALTH: 'h5',
  KIDS: 'h6',
  OTHER: 'neutral',
};

/**
 * The glyph for one Financy category pair. Resolution order: exact `MAIN/SUB` → `MAIN` →
 * (when `main` is null) the null-main sub table → {@link FALLBACK_GLYPH}. Every key in the live
 * taxonomy resolves without reaching the fallback.
 */
export function categoryGlyph(main: string | null | undefined, sub: string | null | undefined): GlyphName {
  if (main === null || main === undefined) {
    if (sub === null || sub === undefined) return FALLBACK_GLYPH;
    return NULL_MAIN_SUB_GLYPHS[sub] ?? FALLBACK_GLYPH;
  }
  if (sub !== null && sub !== undefined) {
    const pair = SUB_GLYPHS[`${main}/${sub}`];
    if (pair !== undefined) return pair;
  }
  return MAIN_GLYPHS[main] ?? FALLBACK_GLYPH;
}

/** The hue for one Financy category pair. Unknown mains are `neutral`, never a decorative hue. */
export function categoryHue(main: string | null | undefined): CategoryHue {
  if (main === null || main === undefined) return 'neutral';
  return MAIN_HUES[main] ?? 'neutral';
}

/** The glyph for a personal display group key. */
export function groupGlyph(groupKey: string): GlyphName {
  return GROUP_GLYPHS[groupKey] ?? FALLBACK_GLYPH;
}

/** The hue for a personal display group key. */
export function groupHue(groupKey: string): CategoryHue {
  return GROUP_HUES[groupKey] ?? 'neutral';
}
